import { useInkind } from '@gik/api/inkinds/inkind';
import { useUserStore } from '@gik/core/store/UserStore';
import bemBlock from '@gik/core/utils/bemBlock';
import { inkindPageTourSelectors, openInkindPageSettingsModal, pageSettingsNavItemIds } from '@gik/inkind-page';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { Button, CircleBone, GikIconName, Popover, SvgIcon } from '@gik/ui';
import React, { useState } from 'react';
import repeatElement from '@gik/core/utils/repeatElement';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';

interface IInkindPagePrivacyBadgeProps {
  inkindRouteId: string;
  canEdit: boolean;
  icon: GikIconName;
  message: string;
}

function InkindPagePrivacyBadge({ icon, message, canEdit, inkindRouteId }: IInkindPagePrivacyBadgeProps) {
  const bem = bemBlock('inkind-page-privacy-badge');
  const [isOpen, setOpen] = useState(false);

  const badge = (
    <Button className={bem()} variant="white" circle block size="base">
      <SvgIcon name={icon} set="gik-icons" />
    </Button>
  );

  return (
    <Popover
      trigger={badge}
      isOpen={isOpen}
      hoverable
      onClick={() => setOpen(true)}
      onHover={() => setOpen(true)}
      onClose={() => setOpen(false)}
      placement={'top'}
    >
      <span>{message}</span>
      {canEdit && (
        <Button
          variant="secondary"
          circle
          className={bem('settings')}
          onClick={() =>
            openInkindPageSettingsModal({ inkindRouteId, scrollToSectionId: pageSettingsNavItemIds.privacy })
          }
        >
          <SvgIcon name="Cog" size="base" />
        </Button>
      )}
    </Popover>
  );
}

interface IInkindPagePrivacyBadgesProps {
  inkindRouteId: string;
}

export default function InkindPagePrivacyBadges({ inkindRouteId }: IInkindPagePrivacyBadgesProps) {
  const { data: inkindPage } = useInkind(inkindRouteId);
  const userId = useUserStore(userStore => userStore.id);
  const canEdit = canUserEditPage(inkindPage, userId);
  const bem = bemBlock('inkind-page-privacy-badges');
  const { t } = useTranslation();

  const badgeOptions = {
    canEdit,
    inkindRouteId,
  };

  return (
    <div id={inkindPageTourSelectors.privacyPreferences} className={bem()}>
      {inkindPage ? (
        <>
          <InkindPagePrivacyBadge
            {...badgeOptions}
            icon={
              inkindPage.phoneCalls
                ? 'PhoneCallsOk'
                : inkindPage.phoneCallRequest.IsTextsOk
                ? 'PhoneCallsWarning'
                : 'PhoneCallsDisabled'
            }
            message={
              inkindPage.phoneCalls
                ? t(translationKeys.PrivacyBadgePhoneCallsOk)
                : inkindPage.phoneCallRequest.IsTextsOk
                ? t(translationKeys.PrivacyBadgePrivacyBadgePhoneCallsAlt, {
                    name: inkindPage.phoneCallRequest.name,
                    phone: inkindPage.phoneCallRequest.phone,
                  })
                : t(translationKeys.PrivacyBadgePhoneCallsDisabled)
            }
          />

          <InkindPagePrivacyBadge
            {...badgeOptions}
            icon={inkindPage.phoneCallRequest.IsTextsOk ? 'TextMessagesOk' : 'TextMessagesDisabled'}
            message={t(
              inkindPage.phoneCallRequest.IsTextsOk
                ? translationKeys.PrivacyBadgeSmsOk
                : translationKeys.PrivacyBadgeSmsDisabled
            )}
          />

          <InkindPagePrivacyBadge
            {...badgeOptions}
            icon={inkindPage.flowers ? 'FlowersOk' : 'FlowersDisabled'}
            message={t(
              inkindPage.flowers ? translationKeys.PrivacyBadgeFlowersOk : translationKeys.PrivacyBadgeFlowersDisabled
            )}
          />

          <InkindPagePrivacyBadge
            {...badgeOptions}
            icon={inkindPage.visitors ? 'VisitorsOk' : 'VisitorsDisabled'}
            message={t(
              inkindPage.visitors
                ? translationKeys.PrivacyBadgeVisitorsOk
                : translationKeys.PrivacyBadgeVisitorsDisabled
            )}
          />

          <InkindPagePrivacyBadge
            {...badgeOptions}
            icon={inkindPage.allowPageSharing ? 'PageSharingOk' : 'PageSharingDisabled'}
            message={t(
              inkindPage.allowPageSharing
                ? translationKeys.PrivacyBadgeShareOk
                : translationKeys.PrivacyBadgeShareDisabled
            )}
          />
        </>
      ) : (
        repeatElement(5, <CircleBone />)
      )}
    </div>
  );
}
